/**
 * The host router — doc 32 §4.5.
 *
 * A pure decision function: given a hostname and a path, say whether to pass the
 * request through, rewrite it onto a mount, or redirect it to the host that owns it.
 * `proxy.ts` executes the decision; all the reasoning lives here so it can be tested
 * without a request object.
 *
 * The rules, in order:
 *
 *   1. Hostname we don't recognise → PASSTHROUGH, unchanged. Load-bearing: this is
 *      what keeps localhost, `*.localhost`, Vercel previews, LAN IPs and tunnels on
 *      path-based routing while production is host-based. The existing comment in
 *      proxy.ts warns against "cleaning it up" — heed it.
 *   2. Host-agnostic prefix (`/api`, `/_next`, `/handoff`, static files) → PASSTHROUGH.
 *   3. Another LIVE host owns this path → 308 there, addressed short-form.
 *   4. Path already under this host's mount → PASSTHROUGH.
 *   5. This host owns it, or nobody does and the host mounts unowned paths → REWRITE
 *      onto `mountPath`.
 *   6. Otherwise → 308 home to www.
 *
 * Rule 3 only fires for LIVE targets. Bouncing a visitor to a host whose DNS does
 * not exist yet would be an outage dressed up as a redirect, so a `planned` owner
 * falls through and the path keeps working where it is. That is what makes the
 * cutover a per-host `status` flip rather than a big-bang switch.
 */

import {
  PLATFORM_HOSTS,
  hostDefinition,
  isCanonicalHost,
  hostOrigin,
  isHostAgnosticPath,
  hostForHiddenMount,
  hiddenMountForPathOnHost,
  platformHostForHostname,
  retiredHostTarget,
  type PlatformHost,
} from '@/lib/routing/hosts';
import {
  resolveHostForPath,
  resolveHostForPathDetailed,
} from '@/lib/business-types/surface-definitions';

export type HostRouteDecision =
  | { kind: 'passthrough' }
  | { kind: 'rewrite'; path: string }
  // 308 for a move between hosts (permanent, and the method must survive it).
  // 307 for a host's own root landing, which is a product decision that may change
  // and must not be cached in browsers as permanent.
  // 301 for a RETIRED hostname (PROP-02 D2): the address is gone, not moved, so
  // it is the one case where the method does not need to survive — a bookmark is
  // a GET, and a 301 says "this address is retired, stop asking".
  | { kind: 'redirect'; url: string; status: 301 | 307 | 308 }
  // A hidden mount reached on the wrong host. NOT a redirect: the mount is
  // served on exactly one hostname and that hostname is not advertised, so
  // answering with a `Location` would defeat the point of hiding it. The visitor
  // gets the same 404 any other nonexistent path on that host would give.
  | { kind: 'notfound' };

export type HostRouteInput = {
  hostname: string;
  pathname: string;
  /** Query string including the leading `?`, or `''`. */
  search?: string;
};

/**
 * Strip a host's mount prefix, since that host's ROOT already is the mount.
 *
 * Exported because the proxy needs it for any redirect it issues to the browser:
 * on `pos.menuraq.com` the POS setup gate must send the user to `/<branch>/setup`,
 * not `/pos/<branch>/setup` — the latter would rewrite to `/pos/pos/<branch>/setup`
 * and 404 (doc 32 P32.8).
 */
export function toHostRelativePath(host: PlatformHost, pathname: string): string {
  return shortFormFor(host, pathname);
}

/**
 * The inverse of `toHostRelativePath`: the path as the APP sees it.
 *
 * On a host-based deployment the browser's pathname is host-relative — the URL bar
 * on dashboard.menuraq.com says `/analytics`, not `/dashboard/analytics`. Any client
 * logic that reasons about "which surface am I on" by matching a path prefix must
 * match against THIS, or it silently matches nothing.
 *
 * That is not hypothetical: `ProtectedRoute` gates on `/^\/dashboard/` and
 * `/^\/admin/`, so on their own hosts those tests would fail and an unauthenticated
 * visitor would be handed the shell with no guard and no redirect. The server-side
 * POS gate had the identical bug (doc 32 P32.8).
 *
 * Returns the pathname unchanged when we are not on a known production host, so dev
 * and previews — which are already path-based — are untouched.
 */
export function toMountedPath(pathname: string, host: PlatformHost | null): string {
  if (!host) return pathname;
  const { mountPath } = hostDefinition(host);
  if (!mountPath) return pathname;
  if (pathname === mountPath || pathname.startsWith(`${mountPath}/`)) return pathname;
  return pathname === '/' ? mountPath : `${mountPath}${pathname}`;
}

/**
 * Normalise ANY href — relative or absolute, this host or another — to the mounted
 * path form, so it can be compared against a mounted pathname.
 *
 * Sidebar active-state compares "where am I" with "where does this link go". Once
 * links became absolute cross-host URLs (an absolute link to the manage host's
 * /menus) while the pathname stayed local (`/menus`), that comparison silently
 * stopped matching and every nav item lost its highlight. Both sides must be
 * expressed the same way.
 */
export function hrefToMountedPath(href: string, fallbackHost: PlatformHost | null): string {
  if (!href) return href;
  try {
    if (/^https?:\/\//i.test(href)) {
      const url = new URL(href);
      const owner = platformHostForHostname(url.hostname);
      return toMountedPath(url.pathname, owner);
    }
  } catch {
    // fall through and treat it as a path
  }
  const path = href.split(/[?#]/)[0];
  return toMountedPath(path, fallbackHost);
}

function shortFormFor(host: PlatformHost, pathname: string): string {
  const { mountPath } = hostDefinition(host);
  if (!mountPath) return pathname;
  if (pathname === mountPath) return '/';
  if (pathname.startsWith(`${mountPath}/`)) return pathname.slice(mountPath.length) || '/';
  return pathname;
}

function redirectTo(host: PlatformHost, pathname: string, search: string): HostRouteDecision {
  const target = new URL(shortFormFor(host, pathname), `https://${hostDefinition(host).hostname}`);
  return { kind: 'redirect', url: `${target.origin}${target.pathname}${search}`, status: 308 };
}

export function decideHostRoute({
  hostname,
  pathname,
  search = '',
}: HostRouteInput): HostRouteDecision {
  // 1. Unknown host — dev, preview, LAN, tunnel. Change nothing.
  const host = platformHostForHostname(hostname);
  if (!host) return { kind: 'passthrough' };

  // 2. Same on every host.
  if (isHostAgnosticPath(pathname)) return { kind: 'passthrough' };

  const def = hostDefinition(host);

  // 2b. A hidden mount reached on any other host. Checked before every redirect
  //     rule below, and before ownership resolution, because `owns` deliberately
  //     does not list it — see `muhid` in PLATFORM_HOSTS. Putting this first is
  //     what guarantees no rule above or below can turn the guess into a pointer.
  const hiddenOwner = hostForHiddenMount(pathname);
  if (hiddenOwner && hiddenOwner !== host) return { kind: 'notfound' };

  // 3. A retired hostname. PROP-02 D2 folded `manage.` and `operations.` into
  //    `dashboard.`; the hostnames stay alive to answer 301 and nothing else.
  //
  //    Ahead of every other rule, including the ownership 308 below, and that
  //    ordering is the whole point: a retired host is never a redirect TARGET
  //    (isCanonicalHost says so), so the ownership rules have one answer only —
  //    dashboard. — and running them first would answer 308 to the same place,
  //    caching a permanent hop that a bookmark then replays forever.
  //
  //    Mounted first, then stripped to the target's SHORT form, which is what
  //    every other redirect here emits: manage. is mounted at /dashboard, so its
  //    host-relative `/menus` becomes dashboard.'s addressable `/menus`. Keeping
  //    the long `/dashboard/menus` would work — the router accepts both — but it
  //    would give one page two external forms depending on which host the visitor
  //    arrived from, and the short form is the one already in bookmarks.
  const retiredTo = retiredHostTarget(host);
  if (retiredTo) {
    return {
      kind: 'redirect',
      url: `${hostOrigin(retiredTo)}${shortFormFor(retiredTo, toMountedPath(pathname, host))}${search}`,
      status: 301,
    };
  }

  // 4. A host whose bare mount is not its own page says where its root goes.
  //    307, not 308: which page is a host's home is a product decision that may
  //    change, and a permanent redirect would be cached in browsers long after.
  if (pathname === '/' && def.home) {
    return { kind: 'redirect', url: `https://${def.hostname}${def.home}${search}`, status: 307 };
  }

  const mountsUnowned = (def.unownedPaths ?? 'mount') === 'mount';
  // A hidden mount is already an app path (`/support` → `app/support`), so a
  // request under one is served as written, exactly like an already-mounted
  // path. Without this the host would classify its own second panel as foreign.
  const hiddenMountHere = hiddenMountForPathOnHost(host, pathname);
  const alreadyMounted =
    Boolean(def.mountPath) &&
    (pathname === def.mountPath || pathname.startsWith(`${def.mountPath}/`));

  // ── Path-based URL (already carries the mount, or the host has no mount) ──
  //
  // Resolve it as written. This is what sends `/dashboard/menus` arriving on
  // dashboard. over to manage.: the two share a mount, so ownership is per-SURFACE
  // and the path alone is not the answer (doc 32 §4.2).
  if (alreadyMounted || !def.mountPath) {
    const owner = resolveHostForPath(pathname);
    if (owner && owner !== host && isCanonicalHost(owner)) {
      return redirectTo(owner, pathname, search);
    }
    if (owner === host || !owner || !isCanonicalHost(owner)) {
      if (!def.mountPath && !owner && !mountsUnowned) {
        return { kind: 'redirect', url: `https://${hostDefinition('www').hostname}${pathname}${search}`, status: 308 };
      }
      return { kind: 'passthrough' };
    }
  }

  // A hidden mount on its OWN host: serve the app path unchanged. Checked before
  // the foreign-path fallbacks below, which would otherwise hand `/support` to
  // www and 404 it by design.
  if (hiddenMountHere) return { kind: 'passthrough' };

  // ── A host that enumerates every surface it serves (Chow) ──
  //
  // Resolved on the RAW path, never the mounted one: `owns` already lists exactly
  // what this host serves, so mounting first would make every unknown path look
  // chow-owned and 404 under the mount instead of going somewhere real.
  if (!mountsUnowned) {
    const rawOwner = resolveHostForPath(pathname);
    if (rawOwner === host) return { kind: 'rewrite', path: toMountedPath(pathname, host) };
    if (rawOwner && isCanonicalHost(rawOwner)) return redirectTo(rawOwner, pathname, search);
    if (pathname === '/') return { kind: 'rewrite', path: def.mountPath };
    return redirectTo('www', pathname, search);
  }

  // ── Host-relative URL, on a host that has a mount ──
  //
  // The host's own namespace is interpreted FIRST: on dashboard. the path
  // `/templates` means `/dashboard/templates`, not the marketing page www owns under
  // the same segment. Resolving the raw path here would hand it to www.
  const mounted = toMountedPath(pathname, host);
  const mountedOwner = resolveHostForPathDetailed(mounted);

  if (mountedOwner?.strength === 'exact') {
    if (mountedOwner.host === host) return { kind: 'rewrite', path: mounted };
    if (isCanonicalHost(mountedOwner.host)) {
      return redirectTo(mountedOwner.host, mounted, search);
    }
    // Owner exists but cannot serve yet — keep it here rather than bounce nowhere.
    return { kind: 'rewrite', path: mounted };
  }

  // Only the bare-mount catch-all claimed it, which is too weak to beat a genuine
  // claim from another host. `/pricing` on dashboard. is the marketing page.
  const rawOwner = resolveHostForPath(pathname);
  if (rawOwner && rawOwner !== host && isCanonicalHost(rawOwner)) {
    return redirectTo(rawOwner, pathname, search);
  }

  if (mountsUnowned) return { kind: 'rewrite', path: mounted };

  // A host that enumerates its surfaces (Chow): an unrecognised path is foreign.
  if (pathname === '/') return { kind: 'rewrite', path: def.mountPath };
  return redirectTo('www', pathname, search);
}
